<template>
  <Teleport to="body">
    <!-- Animated backdrop overlay -->
    <Transition name="fade">
      <div v-if="localOpen" 
           class="fixed inset-0 z-50 bg-black/20 dark:bg-black/50 backdrop-blur-xs"
           @click="handleBackdropClick"></div>
    </Transition>
      
    <!-- Dialog container with slide animation -->
    <Transition name="slide-up">
      <div v-if="localOpen" 
           class="fixed inset-0 z-50 flex items-end justify-center sm:items-center overflow-hidden">
        <div class="w-full sm:max-w-2xl rounded-t-xl sm:rounded-xl border bg-background shadow-lg 
                   max-h-[90vh] flex flex-col"
             @click.stop>
          
          <!-- Header -->
          <div class="flex justify-between items-start p-6 border-b border-border">
            <div class="flex items-center space-x-3">
              <div class="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                <Bot class="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 class="text-xl font-semibold">AI Vehicle Assistant</h2>
                <p class="text-sm text-muted-foreground">
                  Ask me anything about this vehicle
                </p>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <!-- Clear Chat Button -->
              <button 
                v-if="messages.length > 0"
                @click="clearChat"
                class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none text-muted-foreground hover:text-foreground"
                title="Clear conversation"
              >
                <Trash2 class="w-4 h-4" />
                <span class="sr-only">Clear chat</span>
              </button>
              <!-- Close Button -->
              <button 
                @click="handleClose"
                class="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none"
              >
                <X class="w-5 h-5" />
                <span class="sr-only">Close</span>
              </button>
            </div>
          </div>

          <!-- Vehicle Summary -->
          <div v-if="vehicle" class="p-4 bg-muted/30 border-b border-border">
            <div class="flex items-center space-x-4">
              <img 
                v-if="vehicle.image"
                :src="vehicle.image" 
                :alt="vehicle.title"
                class="w-16 h-12 object-cover rounded-md"
              >
              <div class="flex-1">
                <h4 class="font-semibold text-sm">
                  {{ vehicle.title || `${vehicle.year} ${vehicle.make} ${vehicle.model} ${vehicle.badge}`.trim() }}
                </h4>
                <p v-if="vehicle.price" class="text-lg font-bold text-primary">
                  {{ formatPrice(vehicle.price) }}
                </p>
              </div>
            </div>
          </div>

          <!-- Chat Messages Area -->
          <div class="flex-1 overflow-y-auto p-4 space-y-4 min-h-0" ref="messagesContainer">
            <!-- Welcome Message -->
            <div v-if="messages.length === 0" class="flex items-start space-x-3">
              <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot class="w-4 h-4 text-white" />
              </div>
              <div class="flex-1">
                <div class="bg-muted/50 rounded-lg p-3 max-w-md">
                  <p class="text-sm">
                    Hi! I'm your AI vehicle assistant. I can help you with questions about this {{ vehicle?.make }} {{ vehicle?.model }}, including:
                  </p>
                  <ul class="text-sm mt-2 space-y-1 text-muted-foreground">
                    <li>• Features and specifications</li>
                    <li>• Pricing and financing options</li>
                    <li>• Comparisons with similar vehicles</li>
                    <li>• Maintenance and ownership costs</li>
                  </ul>
                  <p class="text-sm mt-2">What would you like to know?</p>
                </div>
              </div>
            </div>

            <!-- Chat Messages -->
            <div v-for="message in messages" :key="message.id" class="flex items-start space-x-3"
                 :class="message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''">
              
              <!-- Avatar -->
              <div class="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                   :class="message.role === 'user' 
                     ? 'bg-primary text-primary-foreground' 
                     : 'bg-gradient-to-br from-blue-500 to-purple-600 text-white'">
                <User v-if="message.role === 'user'" class="w-4 h-4" />
                <Bot v-else class="w-4 h-4" />
              </div>

              <!-- Message Content -->
              <div class="flex-1 max-w-md">
                <div class="rounded-lg p-3"
                     :class="message.role === 'user' 
                       ? 'bg-primary text-primary-foreground ml-auto' 
                       : 'bg-muted/50'">
                  
                  <!-- User Message -->
                  <p v-if="message.role === 'user'" class="text-sm">
                    {{ message.content }}
                  </p>
                  
                  <!-- AI Message with Markdown Support -->
                  <div v-else class="text-sm prose prose-sm max-w-none dark:prose-invert">
                    <div v-html="formatAIMessage(message.content)"></div>
                  </div>
                </div>
                
                <!-- Timestamp -->
                <p class="text-xs text-muted-foreground mt-1"
                   :class="message.role === 'user' ? 'text-right' : 'text-left'">
                  {{ formatTime(message.timestamp) }}
                </p>
              </div>
            </div>

            <!-- Loading Message -->
            <div v-if="isLoading" class="flex items-start space-x-3">
              <div class="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Bot class="w-4 h-4 text-white" />
              </div>
              <div class="flex-1">
                <div class="bg-muted/50 rounded-lg p-3 max-w-md">
                  <div class="flex items-center space-x-2">
                    <Loader2 class="w-4 h-4 animate-spin" />
                    <span class="text-sm text-muted-foreground">AI is thinking...</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Error Message -->
            <div v-if="error" class="flex items-start space-x-3">
              <div class="w-8 h-8 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertCircle class="w-4 h-4 text-white" />
              </div>
              <div class="flex-1">
                <div class="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg p-3 max-w-md">
                  <p class="text-sm text-red-800 dark:text-red-200">
                    {{ error }}
                  </p>
                  <Button 
                    variant="link" 
                    size="sm" 
                    class="p-0 h-auto text-red-600 dark:text-red-400 mt-1"
                    @click="dismissError"
                  >
                    Dismiss
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <!-- Quick Action Buttons -->
          <div v-if="messages.length === 0" class="px-4 pb-2">
            <div class="flex flex-wrap gap-2">
              <Button 
                v-for="suggestion in quickSuggestions" 
                :key="suggestion"
                variant="outline" 
                size="sm"
                class="text-xs"
                @click="sendQuickMessage(suggestion)"
                :disabled="isLoading"
              >
                {{ suggestion }}
              </Button>
            </div>
          </div>

          <!-- Sales Action Buttons -->
          <div v-if="currentSalesIntent && messages.length > 0" class="px-4 pb-2">
            <div class="mb-3 pb-3 border-b border-border">
              <div class="flex flex-wrap gap-2">
                <!-- Test Drive Intent -->
                <template v-if="currentSalesIntent === 'test-drive'">
                  <Button 
                    @click="triggerTestDrive"
                    class="inline-flex items-center gap-2"
                    size="sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Schedule Test Drive
                  </Button>
                  <Button 
                    @click="triggerContactDealer"
                    variant="outline"
                    size="sm"
                    class="inline-flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    Contact Dealer
                  </Button>
                </template>

                <!-- Contact Dealer Intent -->
                <template v-if="currentSalesIntent === 'contact-dealer'">
                  <Button 
                    @click="triggerContactDealer"
                    class="inline-flex items-center gap-2"
                    size="sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    Contact Dealer
                  </Button>
                  <Button 
                    @click="triggerTestDrive"
                    variant="outline"
                    size="sm"
                    class="inline-flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                    </svg>
                    Test Drive
                  </Button>
                </template>

                <!-- Finance Intent -->
                <template v-if="currentSalesIntent === 'finance'">
                  <Button 
                    @click="triggerFinance"
                    class="inline-flex items-center gap-2"
                    size="sm"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                    </svg>
                    Get Finance Quote
                  </Button>
                  <Button 
                    @click="triggerContactDealer"
                    variant="outline"
                    size="sm"
                    class="inline-flex items-center gap-2"
                  >
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
                    </svg>
                    Contact Dealer
                  </Button>
                </template>
              </div>
              <p class="text-xs text-muted-foreground mt-2">
                <span v-if="currentSalesIntent === 'test-drive'">Ready to take the next step?</span>
                <span v-if="currentSalesIntent === 'contact-dealer'">Get in touch with our team</span>
                <span v-if="currentSalesIntent === 'finance'">Explore financing options</span>
              </p>
            </div>
          </div>

          <!-- Input Area -->
          <div class="p-4 border-t border-border">
            <form @submit.prevent="sendMessage" class="flex space-x-2">
              <div class="flex-1 relative">
                <Input
                  v-model="currentMessage"
                  placeholder="Ask me anything about this vehicle..."
                  class="pr-12"
                  :disabled="isLoading"
                  @keydown.enter.prevent="sendMessage"
                />
                <Button
                  type="submit"
                  size="sm"
                  class="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                  :disabled="!currentMessage.trim() || isLoading"
                >
                  <Send class="w-4 h-4" />
                </Button>
              </div>
            </form>
            
            <!-- Input Helper Text -->
            <p class="text-xs text-muted-foreground mt-2 text-center">
              Powered by AI • Information may not always be accurate
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
  
  <!-- Vehicle Action Dialog -->
  <VehicleActionDialog
    :type="actionDialogType"
    :vehicle="props.vehicle"
    :is-open="actionDialogOpen"
    :is-submitting="actionDialogSubmitting"
    @update:open="actionDialogOpen = $event"
    @submit="handleActionDialogSubmit"
  />
</template>

<script setup lang="ts">
import { ref, watch, computed, nextTick, onMounted } from 'vue'
import { 
  X, 
  Bot, 
  User, 
  Send, 
  Loader2, 
  AlertCircle,
  Trash2
} from 'lucide-vue-next'
import VehicleActionDialog from './VehicleActionDialog.vue'
import { useAIVehicleChat } from '~/app/composables/useAIVehicleChat'
// Define ChatMessage interface locally to avoid import issues
interface ChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  timestamp: string
  suggestions?: string[]
  searchResults?: {
    vehicles: any[]
    totalMatches: number
    query: string
  }
}

interface Vehicle {
  id: string
  title?: string
  price?: number
  year?: string | number
  make?: string
  model?: string
  badge?: string
  image?: string
  body?: string
  fuel?: string
  transmission?: string
  condition?: string
  seats?: number | string
  doors?: number | string
  kms?: number
  description?: string
  enrichedData?: any
}

// Extend Window interface for global trigger functions
declare global {
  interface Window {
    triggerTestDrive?: () => void
    triggerContactDealer?: () => void
    triggerFinance?: () => void
  }
}

interface Props {
  vehicle?: Vehicle
  isOpen: boolean
}

interface Emits {
  (e: 'update:open', value: boolean): void
  // Deprecated: These events are handled internally by VehicleActionDialog
  (e: 'trigger-test-drive'): void
  (e: 'trigger-contact-dealer'): void
  (e: 'trigger-finance'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Composables
const { sendMessage: sendAIMessage, isLoading, error, createUserMessage } = useAIVehicleChat()

// Local state
const localOpen = ref(false)
const currentMessage = ref('')
const messages = ref<ChatMessage[]>([])
const messagesContainer = ref<HTMLElement>()
const currentSalesIntent = ref<string | null>(null)

// Vehicle Action Dialog state
const actionDialogOpen = ref(false)
const actionDialogType = ref<'test-drive' | 'contact' | 'finance' | 'inquiry'>('test-drive')
const actionDialogSubmitting = ref(false)

// Chat persistence key based on vehicle ID
const getChatStorageKey = () => {
  return props.vehicle?.id ? `ai-chat-${props.vehicle.id}` : null
}

// Load persisted chat messages
const loadPersistedChat = () => {
  const storageKey = getChatStorageKey()
  
  if (!storageKey || typeof window === 'undefined') {
    return
  }
  
  try {
    const stored = sessionStorage.getItem(storageKey)
    if (stored) {
      const parsedMessages = JSON.parse(stored)
      if (Array.isArray(parsedMessages)) {
        messages.value = parsedMessages
      }
    }
  } catch (error) {
    console.warn('❌ [VehicleAIChatDialog] Failed to load persisted chat:', error)
  }
}

// Save chat messages to session storage
const savePersistedChat = () => {
  const storageKey = getChatStorageKey()
  
  if (!storageKey || typeof window === 'undefined') {
    return
  }
  
  try {
    sessionStorage.setItem(storageKey, JSON.stringify(messages.value))
  } catch (error) {
    console.warn('❌ [VehicleAIChatDialog] Failed to save chat to session storage:', error)
  }
}

// Clear persisted chat for this vehicle
const clearPersistedChat = () => {
  const storageKey = getChatStorageKey()
  
  if (!storageKey || typeof window === 'undefined') {
    return
  }
  
  try {
    sessionStorage.removeItem(storageKey)
  } catch (error) {
    console.warn('❌ [VehicleAIChatDialog] Failed to clear persisted chat:', error)
  }
}

// Watch for parent open state changes
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    localOpen.value = true
  }
}, { immediate: true })

// Quick suggestion buttons
const quickSuggestions = computed(() => {
  if (!props.vehicle) return []
  
  return [
    'Tell me about the key features',
    'What are the running costs?',
    'How does it compare to competitors?',
    'Is this a good deal?',
    'I want to schedule a test drive',
    'How can I contact the dealer?',
    'What are my finance options?'
  ]
})

// Methods
const formatPrice = (price: number): string => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(price)
}

const formatTime = (timestamp: string): string => {
  return new Date(timestamp).toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatAIMessage = (content: string): string => {
  // Safety check for undefined/null content
  if (!content || typeof content !== 'string') {
    return ''
  }
  
  try {
    // Simple markdown-like formatting
    let formattedContent = content
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/\n/g, '<br>')
      .replace(/• /g, '• ')
    
    return formattedContent
  } catch (error) {
    console.error('Error formatting AI message:', error)
    return content // Return original content if formatting fails
  }
}



const scrollToBottom = async () => {
  await nextTick()
  if (messagesContainer.value) {
    messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
  }
}

// Sales action detection
const detectSalesIntent = (message: string): string | null => {
  const lowerMessage = message.toLowerCase()
  
  // Test drive keywords
  if (lowerMessage.includes('test drive') || 
      lowerMessage.includes('book a drive') || 
      lowerMessage.includes('schedule') ||
      lowerMessage.includes('try the car') ||
      lowerMessage.includes('drive it') ||
      lowerMessage.includes('test it')) {
    return 'test-drive'
  }
  
  // Contact dealer keywords
  if (lowerMessage.includes('contact') || 
      lowerMessage.includes('call') || 
      lowerMessage.includes('speak to') ||
      lowerMessage.includes('talk to') ||
      lowerMessage.includes('dealer') ||
      lowerMessage.includes('salesperson') ||
      lowerMessage.includes('phone number') ||
      lowerMessage.includes('get in touch')) {
    return 'contact-dealer'
  }
  
  // Finance keywords
  if (lowerMessage.includes('finance') || 
      lowerMessage.includes('loan') || 
      lowerMessage.includes('payment') ||
      lowerMessage.includes('monthly') ||
      lowerMessage.includes('weekly') ||
      lowerMessage.includes('credit') ||
      lowerMessage.includes('approval') ||
      lowerMessage.includes('deposit') ||
      lowerMessage.includes('interest rate')) {
    return 'finance'
  }
  
  return null
}

// Detect sales intent in AI response content
const detectSalesIntentInResponse = (responseContent: string): string | null => {
  const lowerContent = responseContent.toLowerCase()
  
  // Test drive suggestions in AI response
  if (lowerContent.includes('schedule a test drive') || 
      lowerContent.includes('book a test drive') || 
      lowerContent.includes('would you like to schedule') ||
      lowerContent.includes('test drive to experience') ||
      lowerContent.includes('arrange a test drive') ||
      lowerContent.includes('contact.*sales.*test drive') ||
      lowerContent.includes('firsthand') && lowerContent.includes('test drive')) {
    return 'test-drive'
  }
  
  // Contact dealer suggestions in AI response
  if (lowerContent.includes('contact.*dealer') || 
      lowerContent.includes('speak.*sales') ||
      lowerContent.includes('call.*team') ||
      lowerContent.includes('get in touch') ||
      lowerContent.includes('sales.*consultant') ||
      lowerContent.includes('contact.*team')) {
    return 'contact-dealer'
  }
  
  // Finance suggestions in AI response
  if (lowerContent.includes('finance.*option') || 
      lowerContent.includes('payment.*plan') ||
      lowerContent.includes('loan.*calculator') ||
      lowerContent.includes('monthly.*payment') ||
      lowerContent.includes('financing.*available') ||
      lowerContent.includes('credit.*application')) {
    return 'finance'
  }
  
  return null
}

const sendMessage = async () => {
  if (!currentMessage.value.trim() || isLoading.value) return

  const userMessage = createUserMessage(currentMessage.value.trim())
  messages.value.push(userMessage)
  
  const messageToSend = currentMessage.value.trim()
  currentMessage.value = ''
  
  await scrollToBottom()

  // Check for sales intent before sending to AI
  const salesIntent = detectSalesIntent(messageToSend)

  try {
    // Create enhanced vehicle context for the AI with proper data structure
    const vehicleContext = await createEnhancedVehicleContext(props.vehicle)

    const response = await sendAIMessage(
      messageToSend,
      messages.value,
      vehicleContext
    )

    if (response && response.message) {
      // Handle both string and object message formats
      let messageContent = typeof response.message === 'string' 
        ? response.message 
        : response.message.content || 'Sorry, I encountered an issue processing your request.'
      
      // If we detected sales intent from user message, set the current sales intent
      if (salesIntent) {
        currentSalesIntent.value = salesIntent
      } else {
        // Also check if the AI response itself contains test drive suggestions
        const aiSalesIntent = detectSalesIntentInResponse(messageContent)
        if (aiSalesIntent) {
          currentSalesIntent.value = aiSalesIntent
        }
      }
      
      const assistantMessage: ChatMessage = {
        id: `msg_${Date.now()}_assistant`,
        role: 'assistant',
        content: messageContent,
        timestamp: new Date().toISOString()
      }
      
      // If the AI response includes new search results, add them to the message
      if (response.searchResults) {
        assistantMessage.searchResults = response.searchResults
      }
      
      messages.value.push(assistantMessage)
      
      await scrollToBottom()
    } else {
      console.warn('⚠️ [VehicleAIChatDialog] No message in AI response:', response)
    }
  } catch (err) {
    console.error('❌ [VehicleAIChatDialog] Failed to send message:', err)
    console.error('❌ [VehicleAIChatDialog] Error details:', {
      message: messageToSend,
      vehicle: props.vehicle,
      error: err
    })
  } finally {
    // Save chat after each message exchange
    savePersistedChat()
  }
}



// Action trigger functions
const triggerTestDrive = () => {
  actionDialogType.value = 'test-drive'
  actionDialogOpen.value = true
}

const triggerContactDealer = () => {
  actionDialogType.value = 'contact'
  actionDialogOpen.value = true
}

const triggerFinance = () => {
  actionDialogType.value = 'finance'
  actionDialogOpen.value = true
}

// Set up global functions for button clicks (kept for backward compatibility)
const setupGlobalTriggers = () => {
  if (typeof window !== 'undefined') {
    window.triggerTestDrive = triggerTestDrive
    window.triggerContactDealer = triggerContactDealer
    window.triggerFinance = triggerFinance
  }
}

const sendQuickMessage = (suggestion: string) => {
  currentMessage.value = suggestion
  sendMessage()
}

// Handle vehicle action dialog submission
const handleActionDialogSubmit = async (data: any) => {
  try {
    actionDialogSubmitting.value = true
    
    // Here you would typically send the data to your backend
    // For now, we'll just close the dialog
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // Close the dialog
    actionDialogOpen.value = false
    
  } catch (error) {
    console.error('❌ [VehicleAIChatDialog] Error submitting action dialog:', error)
  } finally {
    actionDialogSubmitting.value = false
  }
}

const handleClose = () => {
  if (isLoading.value) {
    return
  }
  
  localOpen.value = false
  
  setTimeout(() => {
    emit('update:open', false)
  }, 300)
}

const handleBackdropClick = () => {
  if (!isLoading.value) {
    handleClose()
  }
}

const clearChat = () => {
  messages.value = []
  currentSalesIntent.value = null
  clearPersistedChat()
}

const dismissError = () => {
  // Since error is readonly from the composable, we'll just refresh the page or handle it differently
  // For now, we'll just close and reopen the dialog to clear the error state
  handleClose()
}

// Create enhanced vehicle context with store integration
const createEnhancedVehicleContext = async (vehicle?: Vehicle) => {
  if (!vehicle) {
    console.warn('⚠️ [VehicleAIChatDialog] No vehicle provided for context creation')
    return undefined
  }

  try {
    // Try to get enriched vehicle data from store or API
    let enrichedVehicle = vehicle
    
    // Check if we have access to vehicle store for enriched data
    if (typeof window !== 'undefined' && vehicle.id) {
      try {
        // Try to fetch enriched vehicle data
        const enrichmentResponse = await $fetch(`/api/vehicle-enrichment/${vehicle.id}`)
        
        if (enrichmentResponse && (enrichmentResponse as any).enrichedData) {
          enrichedVehicle = { ...vehicle, ...(enrichmentResponse as any).enrichedData }
        }
      } catch (error) {
        console.warn('⚠️ [VehicleAIChatDialog] Could not fetch vehicle enrichment data:', error)
      }
    }

    // Create comprehensive vehicle context
    const vehicleData = {
      id: enrichedVehicle.id,
      title: enrichedVehicle.title || `${enrichedVehicle.year || ''} ${enrichedVehicle.make || ''} ${enrichedVehicle.model || ''} ${enrichedVehicle.badge || ''}`.trim(),
      price: enrichedVehicle.price,
      year: typeof enrichedVehicle.year === 'number' ? enrichedVehicle.year : 
            (enrichedVehicle.year ? parseInt(enrichedVehicle.year.toString()) : undefined),
      
      // Convert simple properties to the expected nested structure that our AI expects
      make: enrichedVehicle.make ? {
        displayValue: [enrichedVehicle.make],
        value: [enrichedVehicle.make]
      } : undefined,
      
      model: enrichedVehicle.model ? {
        displayValue: [enrichedVehicle.model],
        value: [enrichedVehicle.model]
      } : undefined,
      
      badge: enrichedVehicle.badge ? {
        displayValue: [enrichedVehicle.badge],
        value: [enrichedVehicle.badge]
      } : undefined,
      
      // Add enhanced vehicle properties from enrichment data
      body: enrichedVehicle.body ? {
        displayValue: [enrichedVehicle.body],
        value: [enrichedVehicle.body]
      } : { displayValue: ['SUV'], value: ['SUV'] }, // Default fallback
      
      fuel: enrichedVehicle.fuel ? {
        displayValue: [enrichedVehicle.fuel],
        value: [enrichedVehicle.fuel]
      } : { displayValue: ['Petrol'], value: ['Petrol'] }, // Default fallback
      
      transmission: enrichedVehicle.transmission ? {
        displayValue: [enrichedVehicle.transmission],
        value: [enrichedVehicle.transmission]
      } : { displayValue: ['Automatic'], value: ['Automatic'] }, // Default fallback
      
      condition: enrichedVehicle.condition ? {
        displayValue: [enrichedVehicle.condition],
        value: [enrichedVehicle.condition]
      } : { displayValue: ['Used'], value: ['Used'] }, // Default fallback
      
      seats: enrichedVehicle.seats ? {
        displayValue: [enrichedVehicle.seats.toString()],
        value: [enrichedVehicle.seats.toString()]
      } : undefined,
      
      doors: enrichedVehicle.doors ? {
        displayValue: [enrichedVehicle.doors.toString()],
        value: [enrichedVehicle.doors.toString()]
      } : undefined,
      
      // Additional enriched data
      kms: enrichedVehicle.kms || 0,
      images: enrichedVehicle.image ? [enrichedVehicle.image] : [],
      thumb: enrichedVehicle.image,
      description: enrichedVehicle.description || `${enrichedVehicle.year || ''} ${enrichedVehicle.make || ''} ${enrichedVehicle.model || ''} ${enrichedVehicle.badge || ''}`.trim(),
      
      // Add any additional enriched properties
      ...(enrichedVehicle.enrichedData || {})
    }

    const finalContext = {
      vehicles: [vehicleData],
      query: `Information about ${vehicleData.title}`,
      totalMatches: 1
    }
    
    return finalContext
  } catch (error) {
    console.error('❌ [VehicleAIChatDialog] Error creating enhanced vehicle context:', error)
    
    // Fallback to basic vehicle context
    const fallbackContext = {
      vehicles: [{
        id: vehicle.id,
        title: vehicle.title || `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} ${vehicle.badge || ''}`.trim(),
        price: vehicle.price,
        make: vehicle.make ? { displayValue: [vehicle.make], value: [vehicle.make] } : undefined,
        model: vehicle.model ? { displayValue: [vehicle.model], value: [vehicle.model] } : undefined,
        year: typeof vehicle.year === 'number' ? vehicle.year : 
              (vehicle.year ? parseInt(vehicle.year.toString()) : undefined),
        badge: vehicle.badge ? { displayValue: [vehicle.badge], value: [vehicle.badge] } : undefined,
        body: { displayValue: ['SUV'], value: ['SUV'] },
        fuel: { displayValue: ['Petrol'], value: ['Petrol'] },
        transmission: { displayValue: ['Automatic'], value: ['Automatic'] },
        condition: { displayValue: ['Used'], value: ['Used'] },
        kms: 0,
        images: vehicle.image ? [vehicle.image] : [],
        thumb: vehicle.image,
        description: `${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} ${vehicle.badge || ''}`.trim()
      }],
      query: `Information about ${vehicle.year || ''} ${vehicle.make || ''} ${vehicle.model || ''} ${vehicle.badge || ''}`.trim(),
      totalMatches: 1
    }
    
    return fallbackContext
  }
}

// Initialize global triggers when component mounts
onMounted(() => {
  setupGlobalTriggers()
})

// Load persisted chat when dialog opens
watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    loadPersistedChat()
    currentMessage.value = ''
    // Scroll to bottom after loading messages
    nextTick(() => {
      scrollToBottom()
    })
  }
})
</script>

<style scoped>
/* Fade transition for backdrop */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Slide up transition for dialog */
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

/* Custom scrollbar for messages */
.overflow-y-auto::-webkit-scrollbar {
  width: 4px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: hsl(var(--muted));
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: hsl(var(--muted-foreground));
  border-radius: 2px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: hsl(var(--foreground));
}

/* Prose styling for AI messages */
.prose {
  color: inherit;
}

.prose strong {
  color: inherit;
  font-weight: 600;
}

.prose em {
  color: inherit;
}
</style>
